import {memo, useCallback, useMemo, forwardRef, useRef, useLayoutEffect, useEffect} from "react";
import {mergeRefs} from "react-merge-refs";
import {useDropZone} from "@codecks/dnd";
import {cx, getMetaKeyLabel, isWithMetaKey, smoothScrollToNode, TooltipForChild} from "@cdx/common";
import {RawDropArea} from "../uploader/DropArea";
import {api} from "../../lib/api";
import Pill from "./Pill";
import MilestoneBanner from "./MilestoneBanner";
import {useNotifications} from "../../features/notifications/useNotifications";
import xcolors from "../xui/xcolors";
import {moveCardsToDeck} from "../../lib/cross-project-cards-or-decks";
import routes from "../../routes";
import {DeckLikeTileWithUploads} from "./DeckLikeTile";
import {applyDeckAccentColor, deckStyles} from "./deck.css";
import publicProjectRoutes from "../../features/public-project/public-project-routes";
import {hasPermissionToGuardCard, hasPermissionToModifyDeck} from "../../lib/permissions";
import {useInViewportOnce} from "../../lib/hooks/useIsInViewport";
import {pluralize} from "../../lib/utils";
import Emoji from "../Markdown/Emoji";
import {
  Box,
  Col,
  css,
  DSIconCrown,
  DSIconDoc,
  DSIconEye,
  DSIconJourney,
  DSIconShield,
  DSShortcutKey,
} from "@cdx/ds";
import useOriginalCopy from "../../lib/hooks/useOriginalCopy";
import {getDeckCardType, isUserSubscribedToDeck} from "./deck-utils";
import {useHistory, useLocation} from "react-router";
import {getModalUrl} from "../ModalRegistry";
import {cardStateColors, dsColors} from "@cdx/ds/css/tokens";
import {GuardianTooltip} from "../../pages/decks/inspector/inspector-header/DeckGuardiansButton";

const getSlots = ({blockedCards, reviewCards, snoozeCards}) => {
  const slots = [];
  if (blockedCards)
    slots.push(
      <Pill
        type="block"
        tooltip={`This deck contains ${blockedCards} blocked card${blockedCards === 1 ? "" : "s"}`}
      >
        {blockedCards}
      </Pill>
    );
  if (reviewCards)
    slots.push(
      <Pill
        type="review"
        tooltip={`This deck contains ${reviewCards} card${reviewCards === 1 ? "" : "s"} in review`}
      >
        {reviewCards}
      </Pill>
    );
  if (slots.length < 2 && snoozeCards)
    slots.push(
      <Pill
        type="snoozing"
        tooltip={`This deck contains ${snoozeCards} snoozing card${snoozeCards === 1 ? "" : "s"}`}
      >
        {snoozeCards}
      </Pill>
    );
  return slots;
};

const BadgeContainer = ({children, tooltip, className, style}) => (
  <TooltipForChild tooltip={tooltip} delayed>
    <Col
      colorTheme="gray25"
      align="center"
      justify="center"
      bg="foreground"
      rounded="full"
      width="24px"
      height="24px"
      borderWidth={1}
      borderColor="default"
      className={cx(deckStyles.badge, applyDeckAccentColor.asDefaultBorder, className)}
      style={style}
    >
      {children}
    </Col>
  </TooltipForChild>
);

const EmojiTagBadge = ({emoji}) => (
  <BadgeContainer tooltip="Auto project tag active" className={css({size: 14})}>
    <Emoji block>{emoji}</Emoji>
  </BadgeContainer>
);
const ColorTagBadge = ({color}) => (
  <BadgeContainer tooltip="Auto project tag active">
    <Box rounded="full" width="12px" height="12px" style={{backgroundColor: color}} />
  </BadgeContainer>
);
const HeroCardBadge = () => (
  <BadgeContainer
    tooltip="Deck for Hero Cards"
    style={{color: cardStateColors.hero500, background: cardStateColors.hero50}}
  >
    <DSIconCrown size={20} />
  </BadgeContainer>
);
const DocCardBadge = () => (
  <BadgeContainer
    tooltip="Deck for Doc Cards"
    style={{color: cardStateColors.doc500, background: cardStateColors.doc50}}
  >
    <DSIconDoc size={20} />
  </BadgeContainer>
);

const JourneyBadge = ({deck}) => {
  const location = useLocation();
  const history = useHistory();
  const onClick = (e) => {
    if (isWithMetaKey(e)) {
      const to = getModalUrl({
        location,
        modal: `journey.${deck.accountSeq}`,
        state: null,
      });
      history.push(to);
      e.preventDefault();
      e.stopPropagation();
    }
  };
  return (
    <BadgeContainer
      tooltip={
        <Box pa="8px">
          Contains a Journey. <DSShortcutKey size="sm">{getMetaKeyLabel()}</DSShortcutKey> +{" "}
          <DSShortcutKey size="sm">click</DSShortcutKey> to open editor.
        </Box>
      }
      style={{background: "#C5D9ED", color: "#1D4873"}}
    >
      <DSIconJourney size={20} className={css({color: "primary"})} onClick={onClick} />
    </BadgeContainer>
  );
};

const GuardianBadge = ({deckId}) => {
  return (
    <BadgeContainer
      tooltip={() => <GuardianTooltip deckId={deckId} />}
      style={{background: dsColors.active50, color: dsColors.active600}}
    >
      <DSIconShield size={20} />
    </BadgeContainer>
  );
};
const SubscribedBadge = () => (
  <BadgeContainer
    tooltip="You are subscribed to this deck"
    style={{color: dsColors.active600, background: dsColors.active100}}
  >
    <DSIconEye size={20} />
  </BadgeContainer>
);

const CardDropArea = ({canDropCard, isCardOver}) =>
  canDropCard && (
    <RawDropArea isOver={isCardOver} className={deckStyles.dropArea}>
      {isCardOver ? "Move card" : null}
    </RawDropArea>
  );

const DeckContainer = memo(({bag}) => {
  const {
    deckId,
    accountSeq,
    isSelected,
    meId,
    canModifyOrder,
    canModifyDeck,
    to,
    coverFileUrl,
    incompleteCards,
    completeCards,
    isOtherSelected,
    dominantColor,
    isCoverFileLoaded,
    isSubscribed,
    workdays,
    milestoneDate,
    milestoneColor,
    numNewEntries,
    deckNotiCount,
    deckTitle,
    blockedCards,
    reviewCards,
    snoozeCards,
    hasWorkflows,
    dragInfo,
    tagColor,
    tagEmoji,
    nonTransparentBackground,
    hasGuardians,
    deckCardType,
  } = bag;

  const getTooltip = () => {
    const parts = ["This deck contains"];
    const innerp = [];
    if (completeCards > 0) {
      innerp.push(pluralize(completeCards, "completed card"));
    }
    if (incompleteCards > 0) {
      innerp.push(pluralize(incompleteCards, "uncompleted card"));
    }
    if (innerp.length === 0) {
      parts.push(`no cards`);
    } else {
      parts.push(innerp.join(" and "));
    }
    if (deckNotiCount) {
      parts.push("and there are undismissed notifications");
    }
    return parts.join(" ");
  };

  const slots = getSlots({blockedCards, reviewCards, snoozeCards});

  const handleCoverImageUpload = useCallback(
    (fileData) =>
      api.mutate.decks.updateCover({
        id: deckId,
        coverFileData: fileData,
        userId: meId,
      }),
    [deckId, meId]
  );

  const canDropCardFn = (item) => {
    const {cardInfos} = item.data;
    return cardInfos.some(({getCard}) => {
      const card = getCard();
      if (!hasPermissionToGuardCard(api.getRoot(), card)) return false;
      return !(card.deck && card.deck.id === deckId);
    });
  };

  const handleCardDrop = ({item}) => {
    if (!canDropCardFn(item)) return null;
    const {cardInfos} = item.data;
    moveCardsToDeck(
      cardInfos.map((ci) => ci.cardId),
      deckId
    );
  };

  const getDragInfo = () => {
    if (!dragInfo || !canModifyOrder) return false;
    return dragInfo;
  };

  const {
    isOver: isCardOverNow,
    ref: dropRef,
    dragItem,
  } = useDropZone({
    type: "CARD",
    onDrop: handleCardDrop,
  });

  const canDropCard = dragItem && canDropCardFn(dragItem);
  const badges = [];
  if (deckCardType === "hero") badges.push(<HeroCardBadge key="hero" />);
  if (deckCardType === "doc") badges.push(<DocCardBadge key="doc" />);
  if (hasGuardians) badges.push(<GuardianBadge deckId={deckId} key="guardian" />);
  if (tagColor) {
    badges.push(<ColorTagBadge color={tagColor} key="color" />);
  }
  if (tagEmoji) {
    badges.push(<EmojiTagBadge emoji={tagEmoji} key="emoji" />);
  }
  if (hasWorkflows) {
    badges.push(<JourneyBadge key="journey" deck={{accountSeq}} />);
  }
  if (isSubscribed) {
    badges.push(<SubscribedBadge key="subscribed" />);
  }

  return (
    <DeckLikeTileWithUploads
      uploadKey={`deckCover-${deckId}`}
      isSelected={isSelected}
      to={to}
      coverFileUrl={coverFileUrl}
      completeCards={completeCards}
      incompleteCards={incompleteCards}
      isOtherSelected={isOtherSelected}
      dominantColor={dominantColor}
      isCoverFileLoaded={isCoverFileLoaded}
      numNewEntries={numNewEntries}
      deckNotiCount={deckNotiCount}
      title={deckTitle}
      leftSlot={slots[0]}
      rightSlot={slots[1]}
      cardCountTooltip={getTooltip}
      handleCoverImageUpload={handleCoverImageUpload}
      canModify={canModifyDeck}
      canDropCard={canDropCard}
      emptyCoverLabel="Add deck image"
      editModal={{modal: `deckEdit.${deckId}`}}
      badges={badges}
      ref={dropRef}
      dragInfo={getDragInfo()}
      nonTransparentBackground={nonTransparentBackground}
    >
      <MilestoneBanner
        milestoneDate={milestoneDate}
        milestoneColor={milestoneColor}
        workdays={workdays}
      />
      <CardDropArea canDropCard={canDropCard} isCardOver={isCardOverNow} />
    </DeckLikeTileWithUploads>
  );
});

const getTo = ({isSelected, deck}) => {
  if (process.env.REACT_APP_MODE !== "open") {
    return isSelected ? routes.decks.getUrl() : routes.deck.getUrl(deck);
  } else {
    return isSelected
      ? publicProjectRoutes.publicProject.getUrl(deck.project)
      : publicProjectRoutes.publicProjectWithDeck.getUrl(deck.project, deck);
  }
};

const OuterDeck = ({
  deck,
  root,
  isSelected,
  history,
  isOtherSelected,
  dragInfo: passedDragInfo,
  nonTransparentBackground,
  canModifyOrder,
}) => {
  const deckId = deck.id;
  const projectId = deck.project.id;
  const meId =
    process.env.REACT_APP_MODE !== "open" &&
    root.loggedInUser &&
    root.loggedInUser.$meta.get("id", null);
  const coverFileUrl = deck.coverFile && deck.coverFile.$meta.get("url", null);
  const dominantColors = deck.coverFile && deck.coverFile.meta.dominantColors;
  const fallback =
    (dominantColors && dominantColors.length > 0 && dominantColors[0].color) || xcolors.gray600;
  const dominantColor = deck.$meta.get("coverColor", null) || fallback;
  const {accountSeq, hasGuardians} = deck;

  const to = getTo({isSelected, deck});
  const isCoverFileLoaded = deck.$meta.isFieldLoaded("coverFile");

  const isSubscribed = isUserSubscribedToDeck({root, deckId, userId: meId});
  const workdays = root.account.workdays;

  const milestoneDate = deck.milestone && deck.milestone.$meta.get("date", false);
  const milestoneColor = deck.milestone?.$meta.get("color", null);

  const notifications = useNotifications();
  const numNewEntries = (
    (process.env.REACT_APP_MODE !== "open" && notifications.perDeckResolvables[deckId]) ||
    []
  )
    .filter((n) => n.isParticipating)
    .reduce((s, n) => s + n.unseenEntryCount, 0);

  const stats = deck.$meta.get("stats", {});
  const counts = stats.counts || {};
  const workflowItems = stats.workflowItems || {};

  const cardCounts = {
    incomplete:
      (counts.doc || 0) +
      (counts.blocked || 0) +
      (counts.review || 0) +
      (counts.hero || 0) +
      (counts.unassigned || 0) +
      (counts.assigned || 0) +
      (counts.snoozing || 0) +
      (counts.started || 0),
    complete: counts.done || 0,
    review: counts.review || 0,
    blocked: counts.blocked || 0,
    snoozing: counts.snoozing || 0,
  };

  const blockedCards =
    process.env.REACT_APP_MODE === "open" && !deck.project.commentsArePublic
      ? 0
      : cardCounts.blocked;
  const reviewCards =
    process.env.REACT_APP_MODE === "open" && !deck.project.commentsArePublic
      ? 0
      : cardCounts.review;
  const snoozeCards = cardCounts.snoozing;
  const deckNotiCount =
    process.env.REACT_APP_MODE === "open" ? 0 : (notifications.perDeckDiffs[deck.id] || []).length;
  const deckTitle = deck.title;
  const hasWorkflows = process.env.REACT_APP_MODE !== "open" && workflowItems > 0;
  const projectIsPublic = deck.project.isPublic;

  const tag = process.env.REACT_APP_MODE !== "open" && deck.defaultProjectTag;
  const tagColor = tag && tag.$meta.get("color", null);
  const tagEmoji = tag && tag.$meta.get("emoji", null);

  const canModifyDeck =
    process.env.REACT_APP_MODE !== "open" &&
    hasPermissionToModifyDeck({root, project: deck.project});
  const incompleteCards = cardCounts.incomplete;
  const completeCards = root.account.hideCompletedCardCountForDecks ? 0 : cardCounts.complete;
  const dragInfo = useOriginalCopy(passedDragInfo);
  const deckCardType = getDeckCardType(deck);

  const bag = useMemo(
    () => ({
      projectId,
      deckId,
      isSelected,
      isOtherSelected,
      history,
      meId,
      incompleteCards,
      completeCards,
      coverFileUrl,
      dominantColor,
      to,
      isCoverFileLoaded,
      isSubscribed,
      workdays,
      milestoneDate,
      milestoneColor,
      numNewEntries,
      blockedCards,
      reviewCards,
      snoozeCards,
      deckNotiCount,
      deckTitle,
      hasWorkflows,
      projectIsPublic,
      canModifyOrder,
      canModifyDeck,
      dragInfo,
      tagColor,
      tagEmoji,
      nonTransparentBackground,
      accountSeq,
      hasGuardians,
      deckCardType,
    }),
    [
      projectId,
      deckId,
      isSelected,
      isOtherSelected,
      history,
      meId,
      incompleteCards,
      completeCards,
      coverFileUrl,
      dominantColor,
      to,
      isCoverFileLoaded,
      isSubscribed,
      workdays,
      milestoneDate,
      milestoneColor,
      numNewEntries,
      blockedCards,
      reviewCards,
      snoozeCards,
      deckNotiCount,
      deckTitle,
      hasWorkflows,
      projectIsPublic,
      canModifyDeck,
      canModifyOrder,
      dragInfo,
      tagColor,
      tagEmoji,
      nonTransparentBackground,
      accountSeq,
      hasGuardians,
      deckCardType,
    ]
  );

  return <DeckContainer bag={bag} />;
};

const VisibilityCheckDeck = forwardRef((props, ref) => {
  const nodeRef = useRef(null);
  const [visibleRef, isVisible] = useInViewportOnce();
  const mergedRefs = useMemo(
    () => mergeRefs([ref, nodeRef, visibleRef]),
    [ref, nodeRef, visibleRef]
  );
  const {isOtherSelected, deck, isSelected, isDragged} = props;
  const wasSelectedRef = useRef(isSelected);
  useLayoutEffect(() => {
    if (isSelected) {
      smoothScrollToNode(nodeRef.current, {offset: -120});
    } else if (wasSelectedRef.current && !isOtherSelected) {
      smoothScrollToNode(nodeRef.current, {offset: 5, onlyIfInvisible: true});
    }
  }, [isSelected, isOtherSelected]);
  useEffect(() => {
    wasSelectedRef.current = isSelected;
  }, [isSelected]);
  return (
    <div
      ref={mergedRefs}
      className={cx(deckStyles.outer.base, !isVisible && deckStyles.outer.invisible)}
      style={{willChange: "transform"}}
    >
      {isVisible || isDragged ? <OuterDeck {...props} /> : deck?.$meta.get("title", null) ?? null}
    </div>
  );
});

export default VisibilityCheckDeck;
